import {
  CustomerAddress,
  CustomerProfile,
  DeliveryAddress,
  MultiFasciaPreferences,
  Preferences,
} from '@as-react/common-types';
import type { MutationKey, UseMutationOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import type { OptionalMutationOptions, QueryApiOptions } from '../../types/types';

import {
  CustomerUpdateEmailParams,
  updateEmail,
  CustomerSendPasswordResetEmailParams,
  sendPasswordResetEmail,
  CustomerCreateAccountParams,
  createAccount,
  addDeliveryAddress,
  updateDeliveryAddress,
  updatePhoneNumber,
  updateProfile,
  subscribeToNewsletter,
  updatePreferences,
  updateMultiFasciaPreferences,
  updatePassword,
  UpdatePasswordParams,
  deleteDeliveryAddress,
} from './customer.api';

export const customerMutationKeys = {
  all: () => ['customer'] as MutationKey,
  updateEmail: () => [...customerMutationKeys.all(), 'updateEmail'] as MutationKey,
  sendPasswordResetEmail: () => [...customerMutationKeys.all(), 'sendPasswordResetMail'] as MutationKey,
  createAccount: () => [...customerMutationKeys.all(), 'createAccount'] as MutationKey,
  addDeliveryAddress: () => [...customerMutationKeys.all(), 'addDeliveryAddress'] as MutationKey,
  deleteDeliveryAddress: () => [...customerMutationKeys.all(), 'deleteDeliveryAddress'] as MutationKey,
  updateDeliveryAddress: () => [...customerMutationKeys.all(), 'updateDeliveryAddress'] as MutationKey,
  updatePhoneNumber: () => [...customerMutationKeys.all(), 'updatePhoneNumber'] as MutationKey,
  updateProfile: () => [...customerMutationKeys.all(), 'updateProfile'] as MutationKey,
  subscribeNewsletter: () => [...customerMutationKeys.all(), 'subscribeNewsletter'] as MutationKey,
  updatePreferences: () => [...customerMutationKeys.all(), 'updatePreferences'] as MutationKey,
  updateAllPreferences: () => [...customerMutationKeys.all(), 'updateAllPreferences'] as MutationKey,
  updatePassword: () => [...customerMutationKeys.all(), 'updatePassword'] as MutationKey,
};

export const customerMutations = (apiOptions: QueryApiOptions) => ({
  updateEmailMutation: (
    options: OptionalMutationOptions<undefined, CustomerUpdateEmailParams> = {}
  ): UseMutationOptions<undefined, Error, CustomerUpdateEmailParams> => ({
    ...options,
    mutationKey: customerMutationKeys.updateEmail(),
    mutationFn: (params: CustomerUpdateEmailParams) => updateEmail(apiOptions, params),
  }),
  sendPasswordResetEmailMutation: (
    options: OptionalMutationOptions<undefined, CustomerSendPasswordResetEmailParams> = {}
  ): UseMutationOptions<undefined, Error, CustomerSendPasswordResetEmailParams> => ({
    ...options,
    mutationKey: customerMutationKeys.sendPasswordResetEmail(),
    mutationFn: (params: CustomerSendPasswordResetEmailParams) => sendPasswordResetEmail(apiOptions, params),
  }),
  createAccountMutation: (
    options: OptionalMutationOptions<boolean, CustomerCreateAccountParams> = {}
  ): UseMutationOptions<boolean, Error, CustomerCreateAccountParams> => ({
    ...options,
    mutationKey: customerMutationKeys.createAccount(),
    mutationFn: (params: CustomerCreateAccountParams) => createAccount(apiOptions, params),
  }),
  addDeliveryAddressMutation: (
    options: OptionalMutationOptions<unknown, CustomerAddress> = {}
  ): UseMutationOptions<CustomerAddress, Error, CustomerAddress> => ({
    ...options,
    mutationKey: customerMutationKeys.addDeliveryAddress(),
    mutationFn: (params: CustomerAddress) => addDeliveryAddress(apiOptions, params),
  }),
  deleteDeliveryAddressMutation: (
    options: OptionalMutationOptions<unknown, DeliveryAddress> = {}
  ): UseMutationOptions<DeliveryAddress, Error, DeliveryAddress> => ({
    ...options,
    mutationKey: customerMutationKeys.deleteDeliveryAddress(),
    mutationFn: (address: DeliveryAddress) => deleteDeliveryAddress(apiOptions, address),
  }),
  updateDeliveryAddressMutation: (
    options: OptionalMutationOptions<unknown, CustomerAddress> = {}
  ): UseMutationOptions<CustomerAddress, Error, CustomerAddress> => ({
    ...options,
    mutationKey: customerMutationKeys.updateDeliveryAddress(),
    mutationFn: (params: CustomerAddress) => updateDeliveryAddress(apiOptions, params),
  }),
  updatePhoneNumber: (
    options: OptionalMutationOptions<unknown, string> = {}
  ): UseMutationOptions<AxiosResponse, Error, string> => ({
    ...options,
    mutationKey: customerMutationKeys.updatePhoneNumber(),
    mutationFn: (phone: string) => updatePhoneNumber(apiOptions, phone),
  }),
  updateProfileMutation: (
    options: OptionalMutationOptions<unknown, CustomerProfile> = {}
  ): UseMutationOptions<CustomerProfile, Error, CustomerProfile> => ({
    ...options,
    mutationKey: customerMutationKeys.updateProfile(),
    mutationFn: (profile: CustomerProfile) => updateProfile(apiOptions, profile),
  }),
  subscribeNewsletterMutation: (
    options: OptionalMutationOptions<unknown, string> = {}
  ): UseMutationOptions<string, Error, string> => ({
    ...options,
    mutationKey: customerMutationKeys.updateProfile(),
    mutationFn: (email: string) => subscribeToNewsletter(apiOptions, email),
  }),
  updatePreferencesMutation: (
    options: OptionalMutationOptions<unknown, Partial<Preferences>> = {}
  ): UseMutationOptions<unknown, Error, Partial<Preferences>> => ({
    ...options,
    mutationKey: customerMutationKeys.updatePreferences(),
    mutationFn: (preferences: Partial<Preferences>) => updatePreferences(apiOptions, preferences),
  }),
  updateMultiFasciaPreferencesMutation: (
    options: OptionalMutationOptions<unknown, MultiFasciaPreferences> = {}
  ): UseMutationOptions<MultiFasciaPreferences, Error, MultiFasciaPreferences> => ({
    ...options,
    mutationKey: customerMutationKeys.updateAllPreferences(),
    mutationFn: (preferences: MultiFasciaPreferences) => updateMultiFasciaPreferences(apiOptions, preferences),
  }),
  updatePassword: (
    options: OptionalMutationOptions<unknown, UpdatePasswordParams> = {}
  ): UseMutationOptions<void, Error, UpdatePasswordParams> => ({
    ...options,
    mutationKey: customerMutationKeys.updatePassword(),
    mutationFn: (passwords: UpdatePasswordParams) => updatePassword(apiOptions, passwords),
  }),
});
